import Route from '@ember/routing/route';
import { service } from '@ember/service';
import ENV from '../config/environment';
import { isTesting } from '@embroider/macros';

import DacData from '../data/dacdata';

import UrlHandler from '../models/url-handler';
import DebtModel from '../models/debt-model';

import BrowserUtils from '../utils/browser-utils';

import { setNotAvailableMsg } from '../utils/error-utils';
import Constants from '../utils/constants';

import '../utils/array-extension';
import '../utils/number-extension';
import '../utils/string-extension';

const LOCALES = { 'en-us': 'en', 'zh-cn': 'cn', 'ja-jp': 'jp', 'ru-ru': 'ru' };

export default class IndexRoute extends Route {
  @service('aditools-analytics') analytics;
  @service('aditools-site') site;
  @service intl;
  @service cdpDataService;
  @service dataService;
  @service hashService;

  async model(params) {
    const rootURL = ENV.rootURL.endsWith('/') ? ENV.rootURL : `${ENV.rootURL}/`;

    // debtEnvironment options: local, playground, beta, std
    const configURL =
      ENV.debtEnvironment === 'std'
        ? `${rootURL}siteconfig.json`
        : `${rootURL}env-specific/${ENV.debtEnvironment}/siteconfig.json`;
    let htmlElement = document?.querySelector('html');
    let fullLanguage = htmlElement?.getAttribute('lang');
    if (!fullLanguage) fullLanguage = 'en-us';
    const defaultLanguage = params.lang ? params.lang : LOCALES[fullLanguage];
    const invalidBrowser = BrowserUtils.isIEBrowser();

    await this.site.start(configURL, ENV.rootURL, defaultLanguage, this.intl.locales);

    this.intl.setLocale([this.site.FullLocale === 'ru-ru' ? 'en-us' : this.site.FullLocale]);

    this.analytics.start(
      ENV.toolname,
      this.site.AnalyticsUrl,
      false,
      isTesting(),
      ENV.environment === 'development',
      null,
    );

    const urlHandler = new UrlHandler(this.site);

    // Only prepare the model if we are able to use it
    if (!invalidBrowser) return await this.loadApplication(urlHandler);
    else {
      return {
        invalidBrowser: true,
        urlHandler: urlHandler,
      };
    }
  }

  async loadApplication(urlHandler) {
    const notAvailableMsg = (errorParamIntls) =>
      this.intl.t('messages.error_params_not_available', {
        errorParams: errorParamIntls.map((paramIntl) => this.intl.t(`labels.errors.${paramIntl}`)).join(', '),
      });
    setNotAvailableMsg(notAvailableMsg);

    console.time('get parameter mapping data');
    const parameterMappingData = await this.cdpDataService.getParameterMappingData();
    console.timeEnd('get parameter mapping data');

    let signalChainGridData = {};
    const signalChainPsts = Object.keys(DacData.psts)
      .map((pstName) => DacData.psts[pstName])
      .filter((pst) => pst.isInSignalChain);

    console.log('Signal chain PSTs: ', signalChainPsts);

    if (!this.dataService.databook) await this.dataService.loadDatabook();

    console.log('Databook loaded: ', this.dataService.databook);

    const gridDataEntries = await Promise.all(
      signalChainPsts.map(async (pst) => {
        console.time(`get ${pst.id} grid data`);
        const data = await this.dataService.getDatabookGridData(pst, urlHandler.homeUrl);
        console.timeEnd(`get ${pst.id} grid data`);
        return [pst.id, data];
      }),
    );
    signalChainGridData = Object.fromEntries(gridDataEntries);

    console.log('Signal chain grid data loaded: ', signalChainGridData);

    const onModalHandlerCreated = (mHandler) => {
      if (mHandler && mHandler.show && window.location.hash.length <= 1) {
        mHandler.show(Constants.MODAL_TYPES.ABOUT.value);
      }
    };

    let model = new DebtModel(urlHandler, onModalHandlerCreated);

    model.load(parameterMappingData, signalChainGridData);

    await this.hashService.loadFromHash(model);

    model.markAsReady();

    return model;
  }
}
